/**
 * THE APP'S BACKEND - one instance, chosen once, at run time.
 *
 * Everything above `src/backend/` reaches the backend through this, so there
 * is exactly one place that decides which adapter the app is running on. That
 * is the point of the whole port: swapping backends is a config file, not a
 * code change.
 *
 * ADAPTERS LOAD BY DYNAMIC IMPORT, which is not a style choice. A PocketBase
 * deployment must not download the Firebase SDK - 107 KB gzip, a quarter of
 * the bundle - for a backend it will never call, and vice versa. Rollup splits
 * on these two `import()` calls, so each deployment pays only for the one it
 * uses.
 *
 * `backend` is a PROXY over the instance rather than the instance itself.
 * Loading an adapter is asynchronous, but every caller wants the plain
 * `import { backend }` it already had - a proxy keeps that shape while making
 * initialisation explicit, and turns "used before ready" into a loud error at
 * the exact call site rather than an undefined-property crash somewhere else.
 *
 * @see ./config.ts - where the choice comes from
 * @see ./types.ts - the contract every adapter implements
 */
import { loadBackendConfig, type BackendConfig } from './config';
import type { Backend } from './types';

let instance: Backend | null = null;

type Method = 'anonymous' | 'password' | 'google';

/**
 * Ask the deployment what it offers rather than assuming. This is what
 * makes turning a sign-in method on or off a server-side change.
 */
function offeredMethods(pb: import('pocketbase').default): Promise<Method[]> {
  return pb
    .collection('users')
    .listAuthMethods()
    .then((m) => {
      const out: Method[] = ['anonymous'];
      if (m.password?.enabled) out.push('password');
      if (m.oauth2?.providers?.some((p) => p.name === 'google')) out.push('google');
      return out;
    })
    .catch(() => ['anonymous', 'password'] as Method[]);
}

async function createPocketBase(config: BackendConfig): Promise<Backend> {
  const [{ createPocketBaseBackend, PB_SCOPES }, { default: PocketBase }] = await Promise.all([
    import('./pocketbase'),
    import('pocketbase'),
  ]);
  const pb = new PocketBase(config.pocketbase?.url);
  const methods = await offeredMethods(pb);
  // SCOPE IS NOT OPTIONAL FOR THIS SCHEMA. Every session-scoped collection
  // declares `session` as a REQUIRED relation, because an API rule cannot
  // traverse a string to find a session's members. Without this the adapter
  // writes only `_parent` and every scoped write is refused - and the
  // symptom is a session that will not create, nowhere near the cause.
  //
  // `PB_SCOPES` names the relation per root (`sessions/<id>/…` -> `session`).
  // It matches the migration.
  return createPocketBaseBackend({ pb, methods, scopes: PB_SCOPES });
}

async function createFirebase(config: BackendConfig): Promise<Backend> {
  const [{ createFirebaseBackend }, { db, auth, app, storageEmulator, storageOn }] = await Promise.all([
    import('./firebase'),
    import('../firebase.js'),
  ]);
  return createFirebaseBackend({ db, auth, app, storageEmulator, storageOn, methods: config.firebase?.methods });
}

/** Build the adapter the config names. */
async function createBackend(config: BackendConfig): Promise<Backend> {
  return config.backend === 'pocketbase' ? createPocketBase(config) : createFirebase(config);
}

/**
 * Resolve the config and build the backend. Call once, before mounting.
 *
 * Returns the instance as well as storing it, so a caller that wants it
 * directly - a test, a script - does not have to go through the proxy.
 */
export async function initBackend(config?: BackendConfig): Promise<Backend> {
  instance = await createBackend(config ?? (await loadBackendConfig()));
  return instance;
}

/** Test seam: install a backend directly, without config or dynamic imports. */
export function setBackendForTests(b: Backend | null): void {
  instance = b;
}

export const backend: Backend = new Proxy({} as Backend, {
  get(_target, prop) {
    if (!instance) {
      throw new Error(
        `backend.${String(prop)} used before initBackend() - the app must resolve its config before it mounts`,
      );
    }
    return instance[prop as keyof Backend];
  },
});
